import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { MessageService } from 'primeng/api';
import { Card } from 'primeng/card';
import { StepperModule } from 'primeng/stepper';

import { SkeletonBlockComponent } from '../../shared/components/skeleton-block/skeleton-block.component';

import { UsersService } from '../../core/services/users.service';
import { UserStore } from '../../core/services/user-store.service';
import { DescripcionNota } from '../../shared/interfaces/descripcion.interface';
import {
  nextAgregarPersonaStep,
  prevAgregarPersonaStep,
  resolveAgregarPersonaSteps,
} from './agregar-persona.steps';
import {
  AgregarPersonaAntecedentesIglesia,
  AgregarPersonaDatosBasicos,
  AgregarPersonaPasoConfirmacion,
} from './agregar-persona-form.interface';
import { buildAltaSistemaNota } from './helpers/antecedentes-nota.helper';
import { buildCreateUserPayload } from './helpers/build-create-user-payload';
import {
  mapUserToAntecedentes,
  mapUserToConfirmacion,
  mapUserToDatosBasicos,
} from './helpers/map-user-to-agregar-form';
import { AgregarPersonaPasoConfirmacionComponent } from './steps/paso-confirmacion/paso-confirmacion.component';
import { AgregarPersonaPasoDatosBasicosComponent } from './steps/paso-datos-basicos/paso-datos-basicos.component';
import { AgregarPersonaPasoInformacionAdicionalComponent } from './steps/paso-informacion-adicional/paso-informacion-adicional.component';

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

@Component({
  selector: 'app-agregar-persona',
  standalone: true,
  imports: [
    StepperModule,
    Card,
    AgregarPersonaPasoDatosBasicosComponent,
    AgregarPersonaPasoInformacionAdicionalComponent,
    AgregarPersonaPasoConfirmacionComponent,
    SkeletonBlockComponent,
  ],
  templateUrl: './agregar-persona.component.html',
  styleUrl: './agregar-persona.component.scss',
})
export class AgregarPersonaComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly usersService = inject(UsersService);
  private readonly userStore = inject(UserStore);
  private readonly messages = inject(MessageService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

  private readonly editId = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('id'))),
    { initialValue: this.route.snapshot.paramMap.get('id') },
  );

  readonly isEditMode = computed(() => !!this.editId());
  readonly pageTitle = computed(() =>
    this.isEditMode() ? 'Editar persona' : 'Registrar persona',
  );

  readonly steps = computed(() =>
    resolveAgregarPersonaSteps({
      isAdmin: this.userStore.isAdmin(),
      isEdit: this.isEditMode(),
    }),
  );
  readonly activeStep = signal(1);

  stepAfter(current: number): number {
    return nextAgregarPersonaStep(current, this.steps());
  }

  stepBefore(current: number): number {
    return prevAgregarPersonaStep(current, this.steps());
  }
  readonly isMobile = signal(this.mobileQuery.matches);
  readonly datosBasicos = signal<AgregarPersonaDatosBasicos | null>(null);
  readonly antecedentes = signal<AgregarPersonaAntecedentesIglesia | null>(null);
  readonly antecedentesNota = signal<DescripcionNota | null>(null);
  readonly confirmacion = signal<AgregarPersonaPasoConfirmacion | null>(null);
  readonly saving = signal(false);
  readonly loadingPerson = signal(false);
  readonly loadError = signal(false);
  readonly initialDatosBasicos = signal<AgregarPersonaDatosBasicos | null>(null);
  readonly initialAntecedentes = signal<AgregarPersonaAntecedentesIglesia | null>(null);
  readonly initialConfirmacion = signal<AgregarPersonaPasoConfirmacion | null>(null);
  private readonly autorAlta = signal<{ nombre: string; apellido: string } | null>(null);

  private readonly onViewportChange = (event: MediaQueryListEvent): void => {
    this.isMobile.set(event.matches);
  };

  ngOnInit(): void {
    this.mobileQuery.addEventListener('change', this.onViewportChange);
    this.destroyRef.onDestroy(() => {
      this.mobileQuery.removeEventListener('change', this.onViewportChange);
    });

    this.usersService.getCurrentUser().subscribe({
      next: (res) => {
        this.autorAlta.set({
          nombre: res.data.nombre,
          apellido: res.data.apellido,
        });
      },
      error: () => undefined,
    });

    const id = this.editId();
    if (!id) {
      return;
    }

    this.loadingPerson.set(true);
    this.loadError.set(false);
    this.usersService.getById(id).subscribe({
      next: (res) => {
        const user = res.data;
        const datos = mapUserToDatosBasicos(user);
        const antecedentes = mapUserToAntecedentes(user);
        const confirmacion = mapUserToConfirmacion(user);
        this.initialDatosBasicos.set(datos);
        this.initialAntecedentes.set(antecedentes);
        this.initialConfirmacion.set(confirmacion);
        this.datosBasicos.set(datos);
        this.antecedentes.set(antecedentes);
        this.confirmacion.set(confirmacion);
        this.loadingPerson.set(false);
      },
      error: () => {
        this.loadingPerson.set(false);
        this.loadError.set(true);
        this.messages.add({
          severity: 'error',
          summary: 'No se pudo cargar',
          detail: 'No se encontraron los datos de esta persona.',
          life: 5000,
        });
      },
    });
  }

  onStepChange(value: number | undefined): void {
    if (value != null) {
      this.activeStep.set(value);
    }
  }

  onDatosBasicosNext(datos: AgregarPersonaDatosBasicos): void {
    this.datosBasicos.set(datos);
  }

  onAntecedentesChange(data: AgregarPersonaAntecedentesIglesia): void {
    this.antecedentes.set(data);
  }

  onAntecedentesNext(nota: DescripcionNota): void {
    this.antecedentesNota.set(nota);
  }

  onSave(payload: AgregarPersonaPasoConfirmacion): void {
    const datos = this.datosBasicos();
    const session = this.userStore.user();

    if (!datos || !session) {
      this.messages.add({
        severity: 'warn',
        summary: 'Faltan datos',
        detail: 'Completá el paso 1 e iniciá sesión antes de guardar.',
        life: 5000,
      });
      return;
    }

    if (this.saving()) {
      return;
    }

    this.confirmacion.set(payload);
    this.saving.set(true);

    const editId = this.editId();
    const publicar = (autor: { nombre: string; apellido: string } | null): void => {
      const descripcion = editId
        ? undefined
        : [
            this.notaDeAlta(autor),
          ];
      const body = buildCreateUserPayload({
        datos,
        confirmacion: payload,
        session,
        descripcion,
      });
    const onSuccess = (): void => {
      this.saving.set(false);
      this.messages.add({
        severity: 'success',
        summary: editId ? 'Persona actualizada' : 'Persona registrada',
        detail: editId
          ? `${datos.nombre} ${datos.apellido} se guardo correctamente.`
          : `${datos.nombre} ${datos.apellido} se guardo. Entra con ${body.mailAccess} y la contraseña en blanco.`,
        life: 4000,
      });
      void this.router.navigateByUrl('/congregacion');
    };
    const onError = (err: HttpErrorResponse): void => {
      this.saving.set(false);
      this.messages.add({
        severity: 'error',
        summary: 'No se pudo guardar',
        detail: this.resolveCreateError(err),
        life: 6000,
      });
    };

      if (editId) {
        this.usersService.update(editId, body).subscribe({ next: onSuccess, error: onError });
      } else {
        this.usersService.create({ ...body, password: '' }).subscribe({ next: onSuccess, error: onError });
      }
    };

    const autor = this.autorAlta();
    if (autor || editId) {
      publicar(autor);
      return;
    }

    this.usersService.getCurrentUser().subscribe({
      next: (res) => {
        const cargado = { nombre: res.data.nombre, apellido: res.data.apellido };
        this.autorAlta.set(cargado);
        publicar(cargado);
      },
      error: () => publicar(null),
    });
  }

  private notaDeAlta(autor: { nombre: string; apellido: string } | null) {
    const nota = buildAltaSistemaNota({
      autorNombre: autor?.nombre ?? '',
      autorApellido: autor?.apellido ?? '',
      antecedentes: this.antecedentes(),
    });
    return {
      nota: nota.nota,
      hora: nota.hora,
      fecha: nota.fecha,
      autor: nota.autor,
      tipo: nota.tipo,
      leido: nota.leido,
    };
  }

  private resolveCreateError(err: HttpErrorResponse): string {
    if (err.status === 0) {
      return 'No se pudo contactar al servidor.';
    }
    if (err.status === 409) {
      return (
        err.error?.message ??
        'Esa persona o email ya está registrada. Probá con segundo nombre o otro email.'
      );
    }
    if (err.status === 400) {
      return err.error?.message ?? 'Revisá los datos del formulario.';
    }
    return err.error?.message ?? 'Intentá de nuevo en unos minutos.';
  }
}